<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useStockUnitConversion } from '@/composables/useStockUnitConversion'
import { formatRupiah } from '@/utils/formatRupiah'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Inventory',
  },
})

const router = useRouter()
const { loading, errors, request } = useApiRequest()
const { purchasableUnitsFor, toBaseQuantity, computeUnitCost } = useStockUnitConversion()

// ─── Form Data ─────────────────────────────────────────────────────────────────
const form = ref({
  name: '',
  sku: '',
  unit_id: null,
  category_id: null,
  minimum_stock: 0,
  allow_negative_stock: false,
  is_active: true,
})

const formRef = ref()

// ─── Options ───────────────────────────────────────────────────────────────────
const units = ref([])
const categories = ref([])

// Ingredient.unit_id WAJIB satuan dasar (lihat catatan di IngredientRequest) —
// satuan turunan (mis. Kilogram) hanya boleh dipilih sebagai "satuan
// pembelian" di section Stok Awal, bukan sebagai satuan bahan baku itu sendiri.
const baseUnits = computed(() => units.value.filter(u => !u.base_unit_id))

// ─── Stok Awal (opsional) — "Smart HPP Calculator" ──────────────────────────
// Section ini OPSIONAL. Kalau dikosongkan, submit() tidak kirim initial_stock
// sama sekali -> backend berperilaku persis seperti sekarang (stok = 0).
// Kalau diisi, IngredientController::store() memanggil InventoryService::
// setInitialStock() yang SAMA PERSIS dengan tombol "Set Stok Awal" di
// halaman detail — tidak ada logic stok terpisah di sini.
const initialStock = ref({
  purchaseUnitId: null,
  quantity: null,
  totalPrice: null,
  notes: '',
})

// Reset pilihan satuan pembelian tiap kali satuan dasar bahan baku diganti
watch(() => form.value.unit_id, () => {
  initialStock.value.purchaseUnitId = null
})

const purchasableUnits = computed(() => purchasableUnitsFor(units.value, form.value.unit_id))

const selectedPurchaseUnit = computed(() =>
  purchasableUnits.value.find(u => u.id === initialStock.value.purchaseUnitId),
)

const baseUnitSymbol = computed(() => baseUnits.value.find(u => u.id === form.value.unit_id)?.symbol ?? '')

const initialStockBaseQuantity = computed(() =>
  toBaseQuantity(initialStock.value.quantity, selectedPurchaseUnit.value),
)

// HPP per satuan dasar — dihitung ulang REALTIME tiap jumlah/harga berubah.
const initialStockUnitCost = computed(() =>
  computeUnitCost(initialStock.value.totalPrice, initialStockBaseQuantity.value),
)

const hasInitialStockInput = computed(() =>
  !!(initialStock.value.purchaseUnitId || initialStock.value.quantity || initialStock.value.totalPrice),
)

// ─── Fetch options ─────────────────────────────────────────────────────────────
const fetchOptions = async () => {
  const [unitsRes, catsRes] = await Promise.all([
    useApi('/api/v1/units', { query: { per_page: 100 } }).get(),
    useApi('/api/v1/categories', { query: { per_page: 100 } }).get(),
  ])

  units.value = unitsRes?.data ?? []
  categories.value = catsRes?.data ?? []
}

// ─── Submit ────────────────────────────────────────────────────────────────────
const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  const payload = { ...form.value }

  // Opsional: hanya diikutkan kalau user benar-benar mengisi stok awal.
  // Quantity & unit_cost yang dikirim SUDAH dalam satuan dasar (dikonversi
  // di FE) — backend tidak perlu tahu-menahu soal satuan pembelian.
  if (initialStock.value.quantity && initialStock.value.totalPrice) {
    payload.initial_stock = {
      quantity: initialStockBaseQuantity.value,
      unit_cost: initialStockUnitCost.value,
      notes: initialStock.value.notes || undefined,
    }
  }

  const res = await request(() =>
    useApi('/api/v1/ingredients').post({ body: payload }),
  )

  if (res) {
    router.push({ name: 'inventory-ingredients' })
  }
}

onMounted(fetchOptions)
</script>

<template>
  <div>
    <!-- Back Button + Title -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VBtn
          variant="text"
          prepend-icon="ri-arrow-left-line"
          :to="{ name: 'inventory-ingredients' }"
          class="mb-2 px-0"
        >
          Kembali ke Daftar
        </VBtn>
        <h4 class="text-h4 font-weight-medium">Tambah Bahan Baku</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Isi informasi bahan baku. Stok awal opsional — bisa langsung diisi di bawah, atau di-set belakangan dari halaman detail.
        </p>
      </VCol>
    </VRow>

    <VRow>
      <!-- Form Utama -->
      <VCol cols="12" lg="8">
        <VCard>
          <VCardTitle class="pa-6 pb-3 text-h6">Informasi Bahan Baku</VCardTitle>
          <VDivider />

          <VForm ref="formRef" @submit.prevent="submit">
            <VCardText>
              <VRow>
                <!-- Nama -->
                <VCol cols="12" md="8">
                  <VTextField
                    v-model="form.name"
                    label="Nama Bahan Baku *"
                    placeholder="contoh: Biji Kopi Arabica"
                    :rules="[requiredValidator]"
                    :error-messages="errors.name"
                  />
                </VCol>

                <!-- SKU -->
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="form.sku"
                    label="SKU (opsional)"
                    placeholder="BHN-001"
                    :error-messages="errors.sku"
                    hint="Kode unik bahan baku"
                    persistent-hint
                  />
                </VCol>

                <!-- Satuan -->
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="form.unit_id"
                    :items="baseUnits"
                    item-title="name"
                    item-value="id"
                    label="Satuan *"
                    placeholder="Pilih satuan..."
                    hint="Satuan dasar bahan baku (stok & resep selalu pakai ini)"
                    persistent-hint
                    :rules="[requiredValidator]"
                    :error-messages="errors.unit_id"
                  >
                    <template #item="{ props, item }">
                      <VListItem v-bind="props">
                        <template #append>
                          <VChip size="x-small" color="primary" variant="tonal">
                            {{ item.raw.symbol }}
                          </VChip>
                        </template>
                      </VListItem>
                    </template>
                  </VSelect>
                </VCol>

                <!-- Kategori -->
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="form.category_id"
                    :items="categories"
                    item-title="name"
                    item-value="id"
                    label="Kategori (opsional)"
                    placeholder="Pilih kategori..."
                    clearable
                    :error-messages="errors.category_id"
                  />
                </VCol>

                <!-- Minimum Stok -->
                <VCol cols="12" md="6">
                  <VTextField
                    v-model.number="form.minimum_stock"
                    label="Minimum Stok"
                    type="number"
                    min="0"
                    step="0.01"
                    :error-messages="errors.minimum_stock"
                    hint="Notifikasi stok rendah akan muncul jika stok di bawah angka ini"
                    persistent-hint
                  />
                </VCol>

                <!-- Allow Negative Stock -->
                <VCol cols="12" md="6">
                  <div class="mt-2">
                    <VSwitch
                      v-model="form.allow_negative_stock"
                      label="Izinkan Stok Negatif"
                      color="warning"
                      :error-messages="errors.allow_negative_stock"
                    />
                    <p class="text-caption text-medium-emphasis mt-n2 ms-14">
                      Aktifkan jika stok boleh minus (tidak disarankan)
                    </p>
                  </div>
                </VCol>

                <!-- Status Aktif -->
                <VCol cols="12">
                  <VSwitch
                    v-model="form.is_active"
                    label="Status Aktif"
                    color="success"
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VForm>
        </VCard>

        <!-- Stok Awal (Opsional) — "Smart HPP Calculator" -->
        <VCard class="mt-4">
          <VCardTitle class="pa-6 pb-0 text-h6">Stok Awal (Opsional)</VCardTitle>
          <VCardText class="pb-2">
            <p class="text-body-2 text-medium-emphasis mb-4">
              Isi kalau mau langsung set stok awal — dikosongkan berarti bahan baku dibuat
              dengan stok 0 (bisa di-set belakangan dari halaman detail).
            </p>

            <VAlert v-if="!form.unit_id" type="info" variant="tonal" density="compact" class="mb-4">
              Pilih Satuan bahan baku dulu di atas.
            </VAlert>

            <VRow v-else>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="initialStock.quantity"
                  label="Jumlah Pembelian"
                  type="number"
                  min="0"
                  step="any"
                  :rules="hasInitialStockInput ? [v => v > 0 || 'Jumlah harus lebih dari 0'] : []"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VSelect
                  v-model="initialStock.purchaseUnitId"
                  :items="purchasableUnits"
                  item-title="name"
                  item-value="id"
                  label="Satuan"
                >
                  <template #item="{ props, item }">
                    <VListItem v-bind="props">
                      <template #append>
                        <VChip size="x-small" color="primary" variant="tonal">
                          {{ item.raw.symbol }}
                        </VChip>
                      </template>
                    </VListItem>
                  </template>
                </VSelect>
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model.number="initialStock.totalPrice"
                  label="Total Harga Pembelian"
                  type="number"
                  min="0"
                  step="any"
                  prefix="Rp"
                  :rules="hasInitialStockInput ? [v => v > 0 || 'Harga harus lebih dari 0'] : []"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  :model-value="formatRupiah(initialStockUnitCost)"
                  :label="`HPP per ${baseUnitSymbol || 'satuan'} (Otomatis)`"
                  readonly
                  variant="filled"
                  hint="Terhitung otomatis: Total Harga ÷ Jumlah (dikonversi ke satuan dasar)"
                  persistent-hint
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="initialStock.notes"
                  label="Catatan (opsional)"
                  rows="2"
                  placeholder="contoh: Stok awal dari supplier XYZ"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- Actions -->
        <VRow class="mt-2">
          <VCol cols="12" class="d-flex gap-3 justify-end">
            <VBtn variant="tonal" :to="{ name: 'inventory-ingredients' }">Batal</VBtn>
            <VBtn
              color="primary"
              :loading="loading"
              prepend-icon="ri-save-line"
              @click="submit"
            >
              Simpan Bahan Baku
            </VBtn>
          </VCol>
        </VRow>
      </VCol>

      <!-- Info Box -->
      <VCol cols="12" lg="4">
        <VCard variant="tonal" color="info">
          <VCardText>
            <div class="d-flex align-center gap-2 mb-3">
              <VIcon icon="ri-information-line" />
              <span class="font-weight-medium">Catatan Penting</span>
            </div>
            <VList density="compact" bg-color="transparent">
              <VListItem class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="16" class="me-1" />
                  Stok awal opsional — isi sekarang atau nanti dari halaman detail
                </VListItemTitle>
              </VListItem>
              <VListItem class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="16" class="me-1" />
                  SKU harus unik di dalam tenant Anda
                </VListItemTitle>
              </VListItem>
              <VListItem class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <VIcon icon="ri-checkbox-circle-line" color="success" size="16" class="me-1" />
                  Minimum stok digunakan untuk alert peringatan
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
